// Portal auth context — completely separate from the admin auth context.
// A portal user only knows their contactId + agencyId + accountId; they
// never have agency roles or admin surfaces.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "@/lib/api";

export type PortalPrincipal = {
  contactId: number;
  agencyId: string;
  accountId: number;
  email: string;
  name: string;
};
export type PortalMe = {
  portal: PortalPrincipal;
  agency: { id: string; name: string; base_name: string | null };
  account: { id: number; name: string; city: string | null };
};

type Ctx = {
  me: PortalMe | null | undefined; // undefined = loading, null = signed out
  error: string | null;
  verify: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
};

const PortalContext = createContext<Ctx | null>(null);

export function PortalProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<PortalMe | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const r = await api.get<{ portal: PortalPrincipal | null; agency: any; account: any }>("/portal-auth/me");
      if (r.portal) setMe({ portal: r.portal, agency: r.agency, account: r.account });
      else setMe(null);
    } catch {
      setMe(null);
    }
  }
  useEffect(() => { void load(); }, []);

  async function verify(token: string) {
    setError(null);
    try {
      await api.post("/portal-auth/verify", { token });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Verification failed");
      setMe(null);
    }
  }
  async function signOut() {
    try { await api.del("/portal-auth"); } catch { /* ignore */ }
    setMe(null);
  }

  return (
    <PortalContext.Provider value={{ me, error, verify, signOut, reload: load }}>
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const c = useContext(PortalContext);
  if (!c) throw new Error("usePortal must be inside PortalProvider");
  return c;
}
